import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import useTranslation from '../../hooks/useTranslation';
import { formatDate as formatLocaleDate } from '../../utils/formatDate';

// Les formats courts d'en-tete (mois seul, jour+mois) n'existent pas dans le
// helper central : ils gardent toLocaleDateString, mais avec la langue de l'app.
const localeOf = (language) => (language === 'fr' ? 'fr-CA' : 'en-CA');
import { useParams, useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { createTask, updateTask, listTasksByProject, listDependenciesByProject, createDependency, deleteDependency, batchUpdateSortOrder } from '../../services/scheduleService';
import subcontractService from '../../services/subcontractService';
import { projectService } from '../../services/projectService';
import { statusVariant } from '../../utils/statusVariant';
import Badge from '../../components/ui/Badge';
import { Checkbox } from '../../components/ui/Checkbox';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import PortalDropdown from '../../components/ui/PortalDropdown';

// ─── Helpers ────────────────────────────────────────────────────────────────

// ─── Zoom configuration ───────────────────────────────────────────────────
const ZOOM_LEVELS = {
  day:   { dayWidth: 36, label: 'day' },
  week:  { dayWidth: 14, label: 'week' },
  month: { dayWidth: 5,  label: 'month' },
};
const ZOOM_ORDER = ['month', 'week', 'day'];

// Le helper central : il suit la langue de l'app et porte la garde T12:00:00
// qui empeche une date nue de reculer d'un jour au Quebec.
const formatDate = (dateStr, language) => formatLocaleDate(dateStr, language);

const formatDateShort = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  return d?.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const toISO = (date) => {
  const y = date?.getFullYear();
  const m = String(date?.getMonth() + 1)?.padStart(2, '0');
  const d = String(date?.getDate())?.padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const addDays = (dateStr, days) => {
  const d = new Date(dateStr + 'T00:00:00');
  d?.setDate(d?.getDate() + days);
  return toISO(d);
};

// Ecart en jours entre deux dates : sert aux POSITIONS (decalage depuis le debut
// de la ligne de temps, glisser-deposer). Ne pas l'utiliser pour une duree.
const diffDays = (a, b) => {
  const da = new Date(a + 'T00:00:00');
  const db = new Date(b + 'T00:00:00');
  return Math.round((db - da) / 86400000);
};

// Duree INCLUSIVE : debut et fin comptent (du 05 au 13 = 9 j ; 1 jour = 1 j).
// Meme regle que le trigger calculate_task_duration et que la vue Semaine.
const durationDays = (start, end) => diffDays(start, end) + 1;

// Fin d'une tache de N jours qui commence a `start` (inclusive).
const endForDuration = (start, days) => addDays(start, Math.max(1, days) - 1);

// Jalon : tache de duree 0 (debut = fin). Une tache sans date n'en est pas un.
const isMilestone = (task) => !!task?.start_date && task?.start_date === task?.end_date;
const MILESTONE_SIZE = 8;

const buildDays = (startDate, count) => {
  const days = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(startDate.getTime());
    d?.setDate(d?.getDate() + i);
    days?.push(d);
  }
  return days;
};

const TASK_COLORS = [
  { bg: 'bg-orange-400', border: 'border-orange-500', progress: 'bg-orange-600', hex: '#fb923c', light: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-400' },
  { bg: 'bg-blue-400', border: 'border-blue-500', progress: 'bg-blue-600', hex: '#60a5fa', light: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-400' },
  { bg: 'bg-emerald-400', border: 'border-emerald-500', progress: 'bg-emerald-600', hex: '#34d399', light: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-400' },
  { bg: 'bg-violet-400', border: 'border-violet-500', progress: 'bg-violet-600', hex: '#a78bfa', light: 'bg-violet-50', text: 'text-violet-700', dot: 'bg-violet-400' },
  { bg: 'bg-amber-400', border: 'border-amber-500', progress: 'bg-amber-600', hex: '#fbbf24', light: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-400' },
  { bg: 'bg-rose-400', border: 'border-rose-500', progress: 'bg-rose-600', hex: '#fb7185', light: 'bg-rose-50', text: 'text-rose-700', dot: 'bg-rose-400' },
  { bg: 'bg-cyan-400', border: 'border-cyan-500', progress: 'bg-cyan-600', hex: '#22d3ee', light: 'bg-cyan-50', text: 'text-cyan-700', dot: 'bg-cyan-400' },
];

const getColor = (index) => TASK_COLORS?.[index % TASK_COLORS?.length];

// Boutons de la barre d'outils Gantt (actif / « bientot » sans action).
const TOOLBAR_BTN = 'px-2 py-1 rounded-md font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-900 whitespace-nowrap transition-colors';
const TOOLBAR_BTN_SOON = 'px-2 py-1 rounded-md font-medium text-gray-300 cursor-not-allowed whitespace-nowrap';

// Couleurs de phase (Gantt) : une par tache racine, dans l'ordre ; les enfants heritent.
const PHASE_COLORS = ['#34d399', '#a78bfa', '#60a5fa', '#fbbf24', '#f472b6', '#22d3ee'];

// Cercle de progression : grand (groupe, 20 px) ou petit (tache, 14 px).
const ProgressRing = ({ pct = 0, color, size = 'sm' }) => {
  const big = size === 'lg';
  const box = big ? 20 : 14;
  const r = big ? 7 : 5;
  const sw = big ? 3 : 2.5;
  const c = 2 * Math.PI * r;
  const p = Math.max(0, Math.min(100, Number(pct) || 0));
  const mid = box / 2;
  return (
    <svg width={box} height={box} viewBox={`0 0 ${box} ${box}`} className="flex-shrink-0" aria-hidden="true">
      <circle cx={mid} cy={mid} r={r} fill="none" stroke="var(--progress-bg)" strokeWidth={sw} />
      {p > 0 && (
        <circle
          cx={mid} cy={mid} r={r} fill="none" stroke={color} strokeWidth={sw}
          strokeDasharray={c} strokeDashoffset={c - (p / 100) * c}
          strokeLinecap="round" transform={`rotate(-90 ${mid} ${mid})`}
        />
      )}
    </svg>
  );
};

const STATUS_OPTIONS = ['Open', 'In progress', 'Done'];

const STATUS_STYLES = {
  'Open': 'bg-gray-100 text-gray-600',
  'In progress': 'bg-blue-50 text-blue-600',
  'Done': 'bg-emerald-50 text-emerald-600',
};

// Get Monday of the week containing a given date
const getMondayOfWeek = (date) => {
  const d = new Date(date);
  const day = d?.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d?.setDate(d?.getDate() + diff);
  d?.setHours(0, 0, 0, 0);
  return d;
};

const buildWeekDays = (monday) => {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d?.setDate(d?.getDate() + i);
    return d;
  });
};

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// ─── Tree Utilities ──────────────────────────────────────────────────────────

/**
 * Build a tree structure from a flat list using parent_id.
 * Returns root nodes with a `children` array each.
 */
const buildTree = (flatTasks) => {
  const map = {};
  flatTasks?.forEach((t) => { map[t?.id] = { ...t, children: [] }; });

  const roots = [];
  flatTasks?.forEach((t) => {
    if (t?.parent_id && map?.[t?.parent_id]) {
      map?.[t?.parent_id]?.children?.push(map?.[t?.id]);
    } else {
      roots?.push(map?.[t?.id]);
    }
  });
  // Chaque groupe (racine, enfants d'un parent) dans l'ordre choisi (sort_order).
  const sortByOrder = (a, b) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0);
  const sortChildren = (nodes) => {
    nodes?.sort(sortByOrder);
    nodes?.forEach((n) => { if (n?.children?.length > 0) sortChildren(n?.children); });
  };
  sortChildren(roots);
  return roots;
};

/**
 * Flatten a tree into an ordered list respecting expand/collapse state.
 * Each item gets `level` (0-based depth) and `outlineNumber` (e.g. "1.2.3").
 * Also gets `hasChildren` boolean.
 */
const flattenTree = (nodes, expandedIds, level = 0, parentNumber = '') => {
  const result = [];
  nodes?.forEach((node, idx) => {
    const outlineNumber = parentNumber ? `${parentNumber}.${idx + 1}` : `${idx + 1}`;
    const hasChildren = node?.children?.length > 0;
    result?.push({ ...node, level, outlineNumber, hasChildren });
    if (hasChildren && expandedIds?.has(node?.id)) {
      const childRows = flattenTree(node?.children, expandedIds, level + 1, outlineNumber);
      result?.push(...childRows);
    }
  });
  return result;
};

// ─── WeekView Component ──────────────────────────────────────────────────────

const WeekView = ({ tasks, subcontractors, taskColorMap, onEditTask }) => {
  const { t, language } = useTranslation();
  const locale = localeOf(language);
  const [weekStart, setWeekStart] = useState(() => getMondayOfWeek(new Date()));

  const weekDays = buildWeekDays(weekStart);
  const weekDayISOs = weekDays?.map(toISO);

  const prevWeek = () => {
    const d = new Date(weekStart);
    d?.setDate(d?.getDate() - 7);
    setWeekStart(d);
  };

  const nextWeek = () => {
    const d = new Date(weekStart);
    d?.setDate(d?.getDate() + 7);
    setWeekStart(d);
  };

  const goToday = () => setWeekStart(getMondayOfWeek(new Date()));

  const weekLabel = (() => {
    const start = weekDays?.[0];
    const end = weekDays?.[6];
    const opts = { month: 'short', day: 'numeric' };
    if (start?.getFullYear() !== end?.getFullYear()) {
      return `${start?.toLocaleDateString(locale, { ...opts, year: 'numeric' })} – ${end?.toLocaleDateString(locale, { ...opts, year: 'numeric' })}`;
    }
    return `${start?.toLocaleDateString(locale, opts)} – ${end?.toLocaleDateString(locale, { ...opts, year: 'numeric' })}`;
  })();

  const todayISO = toISO(new Date());

  const getSubName = (subId) => {
    if (!subId) return null;
    return subcontractors?.find((s) => s?.id === subId)?.name || null;
  };

  const getTasksForDay = (dayISO) => {
    return tasks?.filter((t) => {
      if (!t?.start_date || !t?.end_date) return false;
      return t?.start_date <= dayISO && t?.end_date >= dayISO;
    });
  };

  const groupBySubcontractor = (dayTasks) => {
    const groups = {};
    dayTasks?.forEach((task) => {
      const subName = getSubName(task?.subcontractor_id) || t('schedule.week.noSubcontractor');
      if (!groups?.[subName]) groups[subName] = [];
      groups?.[subName]?.push(task);
    });
    return groups;
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div className="flex items-center space-x-2">
          <button onClick={prevWeek} className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600 transition-colors" title={t('schedule.week.previous')}>
            <Icon name="ChevronLeft" size={16} />
          </button>
          <button onClick={nextWeek} className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600 transition-colors" title={t('schedule.week.next')}>
            <Icon name="ChevronRight" size={16} />
          </button>
          <span className="text-sm font-semibold text-gray-800 ml-1">{weekLabel}</span>
        </div>
        <button onClick={goToday} className="text-xs font-medium text-orange-500 hover:text-orange-600 border border-orange-200 hover:border-orange-300 px-3 py-1.5 rounded-lg transition-colors">
          {t('projectSchedule.today')}
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="grid grid-cols-7 gap-3 h-full">
          {weekDays?.map((day, dayIdx) => {
            const dayISO = weekDayISOs?.[dayIdx];
            const isToday = dayISO === todayISO;
            const isWeekend = dayIdx >= 5;
            const dayTasks = getTasksForDay(dayISO);
            const grouped = groupBySubcontractor(dayTasks);
            const hasWork = dayTasks?.length > 0;

            return (
              <div key={dayISO} className={`flex flex-col rounded-xl border ${isToday ? 'border-orange-300 bg-orange-50/30' : 'border-gray-200 bg-white'} overflow-hidden`}>
                <div className={`px-3 py-2.5 border-b flex-shrink-0 ${isToday ? 'bg-primary' : isWeekend ? 'bg-gray-50' : 'bg-gray-50'} ${isToday ? 'border-orange-400' : 'border-gray-200'}`}>
                  <p className={`text-xs font-semibold uppercase tracking-wide ${isToday ? 'text-white' : isWeekend ? 'text-gray-400' : 'text-gray-500'}`}>{t('schedule.days')?.[dayIdx] || DAY_LABELS?.[dayIdx]}</p>
                  <p className={`text-lg font-bold leading-tight ${isToday ? 'text-white' : isWeekend ? 'text-gray-400' : 'text-gray-800'}`}>{day?.getDate()}</p>
                  <p className={`text-xs ${isToday ? 'text-orange-100' : 'text-gray-400'}`}>{day?.toLocaleDateString(locale, { month: 'short' })}</p>
                </div>
                <div className="flex-1 p-2 space-y-2 overflow-y-auto">
                  {!hasWork && (
                    <div className="flex items-center justify-center h-full min-h-[60px]">
                      <p className="text-xs text-gray-300 italic">{t('schedule.week.noWork')}</p>
                    </div>
                  )}
                  {Object.entries(grouped)?.map(([subName, subTasks]) => (
                    <div key={subName} className="space-y-1">
                      <p className="text-xs font-semibold text-gray-500 truncate px-1 pt-1">{subName}</p>
                      {subTasks?.map((task) => {
                        const colorIdx = tasks?.findIndex((t) => t?.id === task?.id);
                        const color = getColor(colorIdx >= 0 ? colorIdx : 0);
                        const isMultiDay = task?.start_date !== task?.end_date;
                        return (
                          <button key={task?.id} onClick={() => onEditTask(task)} className={`w-full text-left rounded-lg px-2.5 py-2 border ${color?.light} border-l-4 hover:shadow-sm transition-all group`} style={{ borderLeftColor: color?.hex }}>
                            <p className={`text-xs font-semibold truncate ${color?.text}`}>{task?.name}</p>
                            {isMultiDay && <p className="text-xs text-gray-400 mt-0.5 truncate">{formatDate(task?.start_date, language)} → {formatDate(task?.end_date, language)}</p>}
                            {task?.progress > 0 && (
                              <div className="mt-1.5 h-1 bg-gray-200 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full ${color?.bg} opacity-70`} style={{ width: `${Math.min(100, task?.progress)}%` }} />
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ─── GanttBar Component ──────────────────────────────────────────────────────

const GanttBar = ({ task, taskIndex, timelineStart, colorScheme, onUpdate, rowHeight, onDotMouseDown, dayWidth, variant = 'task' }) => {
  const { t } = useTranslation();
  const dragRef = useRef(null);
  const resizeRef = useRef(null);
  const barRef = useRef(null);

  const startOffset = diffDays(toISO(timelineStart), task?.start_date);
  const duration = task?.duration_days || durationDays(task?.start_date, task?.end_date) || 1;
  const left = startOffset * dayWidth;
  const width = Math.max(duration * dayWidth, dayWidth);
  const progressWidth = Math.min(100, task?.progress || 0);

  const handleDragMouseDown = useCallback((e) => {
    e?.preventDefault();
    e?.stopPropagation();
    const startX = e?.clientX;
    const origStart = task?.start_date;
    const origEnd = task?.end_date;

    const onMove = (me) => {
      const dx = me?.clientX - startX;
      const daysDelta = Math.round(dx / dayWidth);
      if (daysDelta === 0) return;
      const newStart = addDays(origStart, daysDelta);
      const newEnd = addDays(origEnd, daysDelta);
      if (barRef?.current) {
        const newLeft = (diffDays(toISO(timelineStart), newStart)) * dayWidth;
        barRef.current.style.left = `${newLeft}px`;
      }
      dragRef.current = { newStart, newEnd };
    };

    const onUp = async () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      if (dragRef?.current) {
        const { newStart, newEnd } = dragRef?.current;
        dragRef.current = null;
        await onUpdate(task?.id, { start_date: newStart, end_date: newEnd });
      }
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [task, timelineStart, onUpdate, dayWidth]);

  const handleResizeMouseDown = useCallback((e) => {
    e?.preventDefault();
    e?.stopPropagation();
    const startX = e?.clientX;
    const origEnd = task?.end_date;

    const onMove = (me) => {
      const dx = me?.clientX - startX;
      const daysDelta = Math.round(dx / dayWidth);
      if (daysDelta === 0) return;
      const newEnd = addDays(origEnd, daysDelta);
      // Une tache d'un jour (fin = debut) est permise ; la fin ne passe jamais avant le debut.
      if (diffDays(task?.start_date, newEnd) < 0) return;
      if (barRef?.current) {
        const newDuration = durationDays(task?.start_date, newEnd);
        barRef.current.style.width = `${Math.max(newDuration * dayWidth, dayWidth)}px`;
      }
      resizeRef.current = { newEnd };
    };

    const onUp = async () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      if (resizeRef?.current) {
        const { newEnd } = resizeRef?.current;
        resizeRef.current = null;
        await onUpdate(task?.id, { end_date: newEnd });
      }
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [task, onUpdate, dayWidth]);

  const isGroupBar = variant === 'group';
  const dotY = rowHeight / 2;
  const dotLeft = left;
  const dotRight = left + width;

  if (isMilestone(task)) {
    // Le conteneur occupe la case du jour : le glisser-deposer existant (style.left)
    // le deplace tel quel ; le losange est centre dans la case.
    const center = left + dayWidth / 2;
    const halfDiag = Math.round((MILESTONE_SIZE * Math.SQRT2) / 2);
    return (
      <>
        <div
          ref={barRef}
          className="absolute top-0 bottom-0 flex items-center justify-center select-none cursor-grab active:cursor-grabbing"
          style={{ left: `${left}px`, width: `${dayWidth}px` }}
          onMouseDown={handleDragMouseDown}
          title={task?.name}
        >
          <div
            className="flex-shrink-0"
            style={{
              width: MILESTONE_SIZE,
              height: MILESTONE_SIZE,
              background: colorScheme?.hex,
              borderRadius: 1,
              transform: 'rotate(45deg)',
            }}
          />
          <span
            className="absolute text-[11px] text-gray-600 whitespace-nowrap pointer-events-none"
            style={{ left: `${dayWidth / 2 + halfDiag + 6}px`, marginLeft: 4 }}
          >
            {task?.name}
          </span>
        </div>
        <div
          data-dot-side="start"
          className="absolute z-20 w-3 h-3 rounded-full bg-white border-2 cursor-crosshair hover:scale-125 transition-transform"
          style={{ left: `${center - halfDiag - 6}px`, top: `${dotY - 6}px`, borderColor: colorScheme?.hex, boxShadow: `0 0 0 2px ${colorScheme?.hex}33` }}
          onMouseDown={(e) => { e?.preventDefault(); e?.stopPropagation(); onDotMouseDown(e, task, 'start'); }}
          title={t('schedule.tooltips.dragToConnect')}
        />
        <div
          data-dot-side="end"
          className="absolute z-20 w-3 h-3 rounded-full bg-white border-2 cursor-crosshair hover:scale-125 transition-transform"
          style={{ left: `${center + halfDiag - 6}px`, top: `${dotY - 6}px`, borderColor: colorScheme?.hex, boxShadow: `0 0 0 2px ${colorScheme?.hex}33` }}
          onMouseDown={(e) => { e?.preventDefault(); e?.stopPropagation(); onDotMouseDown(e, task, 'end'); }}
          title={t('schedule.tooltips.dragToConnect')}
        />
      </>
    );
  }

  return (
    <>
      {/* Zone de prise de 14 px ; la barre visible fait 7 px (tache) ou 4 px (resume de groupe). */}
      <div
        ref={barRef}
        className="absolute top-1/2 -translate-y-1/2 select-none cursor-grab active:cursor-grabbing"
        style={{ left: `${left}px`, width: `${width}px`, height: '14px', minWidth: `${dayWidth}px` }}
        onMouseDown={handleDragMouseDown}
        title={task?.name}
      >
        <div
          className="absolute inset-x-0 top-1/2 -translate-y-1/2 overflow-hidden"
          style={{ height: isGroupBar ? 4 : 7, borderRadius: isGroupBar ? 2 : 3, background: colorScheme?.hex, opacity: isGroupBar ? 0.45 : 1 }}
        >
          {!isGroupBar && progressWidth > 0 && (
            <div className="absolute inset-y-0 left-0" style={{ width: `${progressWidth}%`, background: 'rgba(0,0,0,0.18)' }} />
          )}
        </div>
        {isGroupBar && (
          <>
            <div className="absolute left-0" style={{ top: 'calc(50% + 2px)', width: 3, height: 3, borderRadius: '0 0 2px 2px', background: colorScheme?.hex, opacity: 0.45 }} />
            <div className="absolute right-0" style={{ top: 'calc(50% + 2px)', width: 3, height: 3, borderRadius: '0 0 2px 2px', background: colorScheme?.hex, opacity: 0.45 }} />
          </>
        )}
        <div className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize rounded-r hover:bg-black/10" onMouseDown={handleResizeMouseDown} />
      </div>
      <div
        data-dot-side="start"
        className="absolute z-20 w-3 h-3 rounded-full bg-white border-2 cursor-crosshair hover:scale-125 transition-transform"
        style={{ left: `${dotLeft - 6}px`, top: `${dotY - 6}px`, borderColor: colorScheme?.hex, boxShadow: `0 0 0 2px ${colorScheme?.hex}33` }}
        onMouseDown={(e) => { e?.preventDefault(); e?.stopPropagation(); onDotMouseDown(e, task, 'start'); }}
        title={t('schedule.tooltips.dragToConnect')}
      />
      <div
        data-dot-side="end"
        className="absolute z-20 w-3 h-3 rounded-full bg-white border-2 cursor-crosshair hover:scale-125 transition-transform"
        style={{ left: `${dotRight - 6}px`, top: `${dotY - 6}px`, borderColor: colorScheme?.hex, boxShadow: `0 0 0 2px ${colorScheme?.hex}33` }}
        onMouseDown={(e) => { e?.preventDefault(); e?.stopPropagation(); onDotMouseDown(e, task, 'end'); }}
        title={t('schedule.tooltips.dragToConnect')}
      />
    </>
  );
};

// ─── ConnectorLines SVG overlay ──────────────────────────────────────────────

// Styles visuels par type de dependance.
const DEP_STYLES = {
  FS: { stroke: '#94a3b8', dash: '4 3' },
  FF: { stroke: '#f59e0b', dash: '6 3' },
  SS: { stroke: '#3b82f6', dash: '6 3' },
  SF: { stroke: '#ef4444', dash: '8 4 2 4' },
};

// Abscisse du debut / de la fin d'une tache sur la ligne de temps.
// Jalon : les pointes du losange (memes positions que ses points de connexion).
const taskEdgeX = (task, side, timelineStart, dayWidth) => {
  const offset = diffDays(toISO(timelineStart), task?.start_date);
  if (isMilestone(task)) {
    const center = offset * dayWidth + dayWidth / 2;
    const halfDiag = Math.round((MILESTONE_SIZE * Math.SQRT2) / 2);
    return side === 'start' ? center - halfDiag : center + halfDiag;
  }
  if (side === 'start') return offset * dayWidth;
  const duration = task?.duration_days || durationDays(task?.start_date, task?.end_date) || 1;
  return (offset + duration) * dayWidth;
};

const ConnectorLines = ({ tasks, dependencies, timelineStart, rowHeight, headerHeight, dayWidth, onDeleteDep }) => {
  if (!dependencies?.length) return null;

  const taskMap = {};
  tasks?.forEach((t, idx) => { taskMap[t?.id] = { task: t, idx }; });

  return (
    <svg className="absolute inset-0 pointer-events-none" style={{ zIndex: 5, overflow: 'visible' }} width="100%" height="100%">
      {dependencies?.map((dep) => {
        const fromEntry = taskMap?.[dep?.depends_on_task_id];
        const toEntry = taskMap?.[dep?.task_id];
        if (!fromEntry || !toEntry) return null;

        const { task: fromTask, idx: fromIdx } = fromEntry;
        const { task: toTask, idx: toIdx } = toEntry;

        const type = DEP_STYLES[dep?.type] ? dep?.type : 'FS';
        const style = DEP_STYLES[type];

        // Depart : fin du predecesseur pour FS/FF, son debut pour SS/SF.
        const fromX = taskEdgeX(fromTask, type === 'FS' || type === 'FF' ? 'end' : 'start', timelineStart, dayWidth);
        // Arrivee : debut du successeur pour FS/SS, sa fin pour FF/SF.
        const toX = taskEdgeX(toTask, type === 'FS' || type === 'SS' ? 'start' : 'end', timelineStart, dayWidth);

        const fromY = headerHeight + fromIdx * rowHeight + rowHeight / 2;
        const toY = headerHeight + toIdx * rowHeight + rowHeight / 2;

        const midX = fromX + (toX - fromX) / 2;
        const pathD = `M ${fromX} ${fromY} L ${midX} ${fromY} L ${midX} ${toY} L ${toX} ${toY}`;

        // La fleche pointe dans le sens du dernier segment.
        const arrowPoints = toX >= midX
          ? `${toX},${toY} ${toX - 6},${toY - 4} ${toX - 6},${toY + 4}`
          : `${toX},${toY} ${toX + 6},${toY - 4} ${toX + 6},${toY + 4}`;

        return (
          <g key={dep?.id}>
            {/* Zone cliquable invisible (12 px) : clic = supprimer la dependance */}
            <path
              d={pathD}
              fill="none"
              stroke="transparent"
              strokeWidth="12"
              style={{ pointerEvents: 'stroke', cursor: 'pointer' }}
              onClick={(e) => { e?.stopPropagation(); onDeleteDep?.(dep); }}
            />
            <path d={pathD} fill="none" stroke={style.stroke} strokeWidth="1.5" strokeDasharray={style.dash} opacity="0.8" />
            <polygon points={arrowPoints} fill={style.stroke} opacity="0.8" />
            <text
              x={midX}
              y={Math.min(fromY, toY) - 6}
              textAnchor="middle"
              className="text-[9px] font-semibold select-none"
              fill={style.stroke}
              opacity="0"
            >
              {type}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

// ─── DragLine SVG ─────────────────────────────────────────────────────────────

const DragLine = ({ from, to }) => {
  if (!from || !to) return null;
  return (
    <svg className="fixed inset-0 pointer-events-none" style={{ zIndex: 9999 }} width="100vw" height="100vh">
      <line x1={from.x} y1={from.y} x2={to?.x} y2={to?.y} stroke="#f97316" strokeWidth="2" strokeDasharray="6 3" opacity="0.9" />
      <circle cx={from.x} cy={from.y} r="4" fill="#f97316" />
      <circle cx={to?.x} cy={to?.y} r="4" fill="#f97316" opacity="0.6" />
    </svg>
  );
};

// ─── Inline Cell Components ──────────────────────────────────────────────────

const EditableCell = ({ value, onSave, type = 'text', className = '' }) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [localVal, setLocalVal] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => { setLocalVal(value); }, [value]);

  const commit = () => {
    setEditing(false);
    if (localVal !== value) onSave(localVal);
  };

  // Un clic SELECTIONNE la cellule (focus visible) ; double-clic ou Entree pour
  // modifier. Echap ou sortie du champ referment l'edition.
  const startEdit = () => { setEditing(true); setTimeout(() => inputRef?.current?.focus(), 0); };
  if (!editing) {
    return (
      <div
        tabIndex={0}
        className={`cursor-default hover:bg-gray-50 rounded px-1 py-0.5 truncate outline-none focus:bg-blue-50/30 focus:ring-1 focus:ring-blue-300/40 ${className}`}
        onDoubleClick={startEdit}
        onKeyDown={(e) => { if (e?.key === 'Enter') { e?.preventDefault(); startEdit(); } }}
        title={value ? `${value} — ${t('schedule.tooltips.clickToEdit')}` : t('schedule.tooltips.clickToEdit')}
      >
        {value || <span className="text-gray-300 italic text-xs">—</span>}
      </div>
    );
  }

  return (
    <input
      ref={inputRef}
      type={type}
      value={localVal || ''}
      onChange={(e) => setLocalVal(e?.target?.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e?.key === 'Enter') commit(); if (e?.key === 'Escape') { setEditing(false); setLocalVal(value); } }}
      className={`w-full border border-orange-300 rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white ${className}`}
    />
  );
};

const StatusCell = ({ value, onSave }) => {
  const [editing, setEditing] = useState(false);
  const selectRef = useRef(null);

  useEffect(() => {
    if (editing) setTimeout(() => selectRef?.current?.focus(), 0);
  }, [editing]);

  const statusStyle = STATUS_STYLES?.[value] || 'bg-gray-100 text-gray-600';

  if (!editing) {
    return (
      <div className="cursor-pointer" onClick={() => setEditing(true)}>
        <Badge variant={statusVariant('schedule', value || 'Open')}>
          {value || 'Open'}
        </Badge>
      </div>
    );
  }

  return (
    <select
      ref={selectRef}
      value={value || 'Open'}
      onChange={(e) => { onSave(e?.target?.value); setEditing(false); }}
      onBlur={() => setEditing(false)}
      className="w-full border border-orange-300 rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white"
    >
      {STATUS_OPTIONS?.map((s) => <option key={s} value={s}>{s}</option>)}
    </select>
  );
};

const DurationCell = ({ task, onSave }) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [localVal, setLocalVal] = useState('');
  const inputRef = useRef(null);

  const computedDuration = task?.duration_days || (task?.start_date && task?.end_date ? durationDays(task?.start_date, task?.end_date) : null);

  useEffect(() => { setLocalVal(String(computedDuration || '')); }, [computedDuration]);

  const commit = () => {
    setEditing(false);
    const days = parseInt(localVal, 10);
    if (!isNaN(days) && days > 0 && task?.start_date) {
      const newEnd = endForDuration(task?.start_date, days);
      onSave({ end_date: newEnd, duration_days: days });
    }
  };

  if (isMilestone(task)) {
    return (
      <div className="px-1 py-0.5 text-center">
        <span className="text-xs text-amber-600 font-medium whitespace-nowrap">◆ {t('schedule.milestoneLabel')}</span>
      </div>
    );
  }

  if (!editing) {
    return (
      <div
        tabIndex={0}
        className="cursor-default hover:bg-gray-50 rounded px-1 py-0.5 text-center outline-none focus:bg-blue-50/30 focus:ring-1 focus:ring-blue-300/40"
        onDoubleClick={() => { setEditing(true); setTimeout(() => inputRef?.current?.focus(), 0); }}
        onKeyDown={(e) => { if (e?.key === 'Enter') { e?.preventDefault(); setEditing(true); setTimeout(() => inputRef?.current?.focus(), 0); } }}
        title={t('schedule.tooltips.clickToEditDuration')}
      >
        {!computedDuration || computedDuration < 1
          ? <span className="text-gray-300 italic text-xs">—</span>
          : <span className="text-xs text-gray-700">{computedDuration}d</span>}
      </div>
    );
  }

  return (
    <input
      ref={inputRef}
      type="number"
      min="1"
      value={localVal}
      onChange={(e) => setLocalVal(e?.target?.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e?.key === 'Enter') commit(); if (e?.key === 'Escape') { setEditing(false); } }}
      className="w-full border border-orange-300 rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white text-center"
    />
  );
};

// ─── Task Table Component (colonnes fixes masquables + hierarchie) ──────────

const TaskTable = ({ rows, subcontractors, rowHeight, headerHeight, onUpdateTask, expandedIds, onToggleExpand, onAddRow, onMoveTask, firstSiblingIds, lastSiblingIds, scrollRef, visibleCols, onToggleCol }) => {
  const { t } = useTranslation();
  const gearRef = useRef(null);
  const [colMenuOpen, setColMenuOpen] = useState(false);
  const getSubName = (subId) => {
    if (!subId) return '—';
    return subcontractors?.find((s) => s?.id === subId)?.name || '—';
  };

  // Colonnes a largeur fixe, masquables via le menu ⚙ (Assigne / Statut masquees par defaut).
  const COL_OPTIONS = [
    { key: 'start', label: t('schedule.colStart') },
    { key: 'end', label: t('schedule.colEnd') },
    { key: 'dur', label: t('schedule.colDuration') },
    { key: 'pct', label: t('schedule.colProgress') },
    { key: 'assigned', label: t('schedule.table.assigned') },
    { key: 'status', label: t('schedule.table.status') },
  ];
  const REORDER_W = 20;
  const COL_W = { assigned: 110, status: 96, start: 80, end: 80, dur: 48, pct: 70, gear: 28 };
  const minWidth = REORDER_W + 160 + COL_W.gear + ['assigned', 'status', 'start', 'end', 'dur', 'pct']
    .reduce((sum, k) => sum + (visibleCols?.[k] ? COL_W[k] : 0), 0);

  const headCell = 'flex-shrink-0 px-2 text-center';
  const cell = 'flex-shrink-0 px-1 min-w-0 text-center';

  const renderAddRow = (row) => (
    <div
      key={row?.id}
      className="flex items-center gap-1.5 border-b border-gray-100 text-[11px]"
      style={{ height: rowHeight, paddingLeft: row?.parentTask ? 36 : 14 }}
    >
      <button type="button" onClick={() => onAddRow('task', row?.parentTask)} className="flex items-center gap-1 font-medium opacity-70 hover:opacity-100 hover:underline" style={{ color: 'var(--fg-link)' }}>
        <span aria-hidden="true">+</span>{t('schedule.add.task')}
      </button>
      <span className="text-gray-300 text-[10px]">|</span>
      <button type="button" onClick={() => onAddRow('milestone', row?.parentTask)} className="flex items-center gap-1 font-medium opacity-70 hover:opacity-100 hover:underline" style={{ color: 'var(--fg-link)' }}>
        <span aria-hidden="true">◇</span>{t('schedule.add.milestone')}
      </button>
      <span className="text-gray-300 text-[10px]">|</span>
      <button type="button" onClick={() => onAddRow('section', row?.parentTask)} className="flex items-center gap-1 font-medium opacity-70 hover:opacity-100 hover:underline" style={{ color: 'var(--fg-link)' }}>
        <span aria-hidden="true">☰</span>{t('schedule.add.section')}
      </button>
    </div>
  );

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Table Header */}
      <div
        className="flex-shrink-0 bg-gray-50 border-b border-gray-200 flex items-center text-[10px] font-semibold text-gray-500 uppercase tracking-wide select-none"
        style={{ height: headerHeight, minWidth }}
      >
        <div className="flex-shrink-0" style={{ width: REORDER_W }} />
        <div className="flex-1 min-w-0 px-2 truncate">{t('schedule.table.task')}</div>
        {visibleCols?.assigned && <div data-col="assigned" className={headCell} style={{ width: COL_W.assigned }}>{t('schedule.table.assigned')}</div>}
        {visibleCols?.status && <div data-col="status" className={headCell} style={{ width: COL_W.status }}>{t('schedule.table.status')}</div>}
        {visibleCols?.start && <div data-col="start" className={headCell} style={{ width: COL_W.start }}>{t('schedule.table.start')}</div>}
        {visibleCols?.end && <div data-col="end" className={headCell} style={{ width: COL_W.end }}>{t('schedule.table.end')}</div>}
        {visibleCols?.dur && <div data-col="dur" className={headCell} style={{ width: COL_W.dur }}>{t('schedule.table.duration')}</div>}
        {visibleCols?.pct && <div data-col="pct" className={headCell} style={{ width: COL_W.pct }}>%</div>}
        <div className="flex-shrink-0 flex items-center justify-center" style={{ width: COL_W.gear }}>
          <button
            ref={gearRef}
            type="button"
            onClick={() => setColMenuOpen((o) => !o)}
            className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100"
            title={t('schedule.columns')}
            aria-label={t('schedule.columns')}
          >
            <Icon name="Settings" size={13} />
          </button>
        </div>
      </div>
      <PortalDropdown anchorRef={gearRef} open={colMenuOpen} onClose={() => setColMenuOpen(false)} width={200}>
        <div className="py-1.5">
          <div className="px-3.5 pt-1 pb-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">{t('schedule.columns')}</div>
          {COL_OPTIONS.map((opt) => (
            <div key={opt.key} className="px-3.5 py-1.5 hover:bg-gray-50">
              <Checkbox
                id={`schedule-col-${opt.key}`}
                checked={!!visibleCols?.[opt.key]}
                onChange={() => onToggleCol(opt.key)}
                label={opt.label}
              />
            </div>
          ))}
        </div>
      </PortalDropdown>
      {/* Table Rows */}
      <div ref={scrollRef} className="overflow-y-auto overflow-x-auto flex-1">
        <div style={{ minWidth }}>
          {rows?.map((task) => {
            if (task?.rowKind === 'add') return renderAddRow(task);

            const isGroup = task?.rowKind === 'group';
            const isExpanded = expandedIds?.has(task?.id);
            const pct = isGroup ? (task?.groupProgress ?? 0) : Math.max(0, Math.min(100, Number(task?.progress) || 0));
            // Groupe : pas de retrait. Tache enfant : 28 px (+16 px par niveau supplementaire).
            const level = task?.level || 0;
            const namePad = level === 0 ? 8 : 28 + (level - 1) * 16;
            const dateCls = isGroup ? 'text-[11px] font-semibold text-gray-800 tabular-nums' : 'text-[11px] text-gray-500 tabular-nums';

            return (
              <div
                key={task?.id}
                className={`group flex items-center border-b border-gray-100 transition-colors ${isGroup ? 'hover:brightness-[0.98]' : 'hover:bg-gray-50/60'}`}
                style={{
                  height: rowHeight,
                  borderLeft: `3px solid ${isGroup ? task?.color : 'transparent'}`,
                  background: isGroup ? 'var(--bg-group)' : undefined,
                }}
              >
                {/* Reordonnement — monter / descendre parmi les taches du meme parent */}
                <div className="flex-shrink-0 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity" style={{ width: REORDER_W - 3 }}>
                  <button
                    type="button"
                    onClick={(e) => { e?.stopPropagation(); onMoveTask?.(task?.id, 'up'); }}
                    className="p-0 text-gray-400 hover:text-gray-700 disabled:invisible"
                    disabled={firstSiblingIds?.has(task?.id)}
                    title={t('schedule.reorder.moveUp')}
                    aria-label={t('schedule.reorder.moveUp')}
                  >
                    <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M2 8L6 4L10 8" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e?.stopPropagation(); onMoveTask?.(task?.id, 'down'); }}
                    className="p-0 text-gray-400 hover:text-gray-700 disabled:invisible"
                    disabled={lastSiblingIds?.has(task?.id)}
                    title={t('schedule.reorder.moveDown')}
                    aria-label={t('schedule.reorder.moveDown')}
                  >
                    <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M2 4L6 8L10 4" />
                    </svg>
                  </button>
                </div>

                {/* Task Name */}
                <div className="flex-1 min-w-0 flex items-center gap-1.5 pr-2" style={{ paddingLeft: namePad }}>
                  {task?.hasChildren ? (
                    <button
                      type="button"
                      onClick={() => onToggleExpand(task?.id)}
                      className="flex-shrink-0 w-4 h-4 flex items-center justify-center text-[10px] text-gray-500 hover:text-gray-800"
                      title={isExpanded ? t('schedule.tooltips.collapse') : t('schedule.tooltips.expand')}
                      aria-label={isExpanded ? t('schedule.tooltips.collapse') : t('schedule.tooltips.expand')}
                    >
                      {isExpanded ? '▾' : '▸'}
                    </button>
                  ) : level === 0 ? (
                    <div className="flex-shrink-0 w-4" />
                  ) : null}
                  {isGroup && <ProgressRing pct={pct} color={task?.color} size="lg" />}
                  {!isGroup && isMilestone(task) && (
                    <span className="flex-shrink-0 inline-block" style={{ width: 8, height: 8, borderRadius: 1, transform: 'rotate(45deg)', background: task?.color }} />
                  )}
                  <EditableCell
                    value={task?.name}
                    onSave={(val) => onUpdateTask(task?.id, { name: val })}
                    className={`flex-1 min-w-0 ${isGroup ? 'text-xs font-bold text-gray-900' : 'text-xs text-gray-700'}`}
                  />
                </div>

                {/* Assigned */}
                {visibleCols?.assigned && (
                  <div data-col="assigned" className={cell} style={{ width: COL_W.assigned }}>
                    <span className="text-[11px] text-gray-600 block truncate" title={getSubName(task?.subcontractor_id)}>
                      {getSubName(task?.subcontractor_id)}
                    </span>
                  </div>
                )}

                {/* Status */}
                {visibleCols?.status && (
                  <div data-col="status" className={cell} style={{ width: COL_W.status }}>
                    <StatusCell
                      value={task?.status || 'Open'}
                      onSave={(val) => onUpdateTask(task?.id, { status: val })}
                    />
                  </div>
                )}

                {/* Start Date */}
                {visibleCols?.start && (
                  <div data-col="start" className={cell} style={{ width: COL_W.start }}>
                    <EditableCell
                      value={task?.start_date || ''}
                      type="date"
                      onSave={(val) => {
                        if (val && task?.end_date && val <= task?.end_date) {
                          onUpdateTask(task?.id, { start_date: val });
                        } else if (val) {
                          onUpdateTask(task?.id, { start_date: val, end_date: val });
                        }
                      }}
                      className={dateCls}
                    />
                  </div>
                )}

                {/* End Date */}
                {visibleCols?.end && (
                  <div data-col="end" className={cell} style={{ width: COL_W.end }}>
                    <EditableCell
                      value={task?.end_date || ''}
                      type="date"
                      onSave={(val) => {
                        if (val && task?.start_date && val >= task?.start_date) {
                          onUpdateTask(task?.id, { end_date: val });
                        }
                      }}
                      className={dateCls}
                    />
                  </div>
                )}

                {/* Duration */}
                {visibleCols?.dur && (
                  <div data-col="dur" className={cell} style={{ width: COL_W.dur }}>
                    <DurationCell
                      task={task}
                      onSave={(updates) => onUpdateTask(task?.id, updates)}
                    />
                  </div>
                )}

                {/* % Avancement */}
                {visibleCols?.pct && (
                  <div
                    data-col="pct"
                    className="flex-shrink-0 flex items-center justify-end gap-1 pr-1"
                    style={{ width: COL_W.pct }}
                    title={t('schedule.progress')}
                  >
                    <ProgressRing pct={pct} color={task?.color} size={isGroup ? 'lg' : 'sm'} />
                    <span className={`text-[10px] tabular-nums text-right ${isGroup ? 'font-semibold text-gray-800' : 'font-medium text-gray-500'}`} style={{ minWidth: 28 }}>
                      {pct}%
                    </span>
                  </div>
                )}

                <div className="flex-shrink-0" style={{ width: COL_W.gear }} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ─── Main SchedulePage ───────────────────────────────────────────────────────

const SchedulePage = () => {
  const { t, language } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [dependencies, setDependencies] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [viewMode, setViewMode] = useState('gantt');

  // ── Expand/collapse state ────────────────────────────────────────────────
  const [expandedTaskIds, setExpandedTaskIds] = useState(new Set());

  const handleToggleExpand = useCallback((taskId) => {
    setExpandedTaskIds((prev) => {
      const next = new Set(prev);
      if (next?.has(taskId)) {
        next?.delete(taskId);
      } else {
        next?.add(taskId);
      }
      return next;
    });
  }, []);

  // ── Build tree + flattened visible list ──────────────────────────────────
  const flattenedTasks = useMemo(() => {
    const tree = buildTree(tasks);
    return flattenTree(tree, expandedTaskIds);
  }, [tasks, expandedTaskIds]);

  // ── Resizable panel state ────────────────────────────────────────────────
  const [leftPanelWidth, setLeftPanelWidth] = useState(55); // percentage
  const containerRef = useRef(null);
  const panelDragRef = useRef(null);

  // ── Lignes affichees (Gantt) : groupes, taches et rangees d'ajout ──────
  // Groupe = tache racine qui a des enfants. Couleur de phase par racine, heritee
  // par les enfants. Une rangee d'ajout sous chaque groupe deplie + une en bas.
  // Les rangees d'ajout gardent leur place dans la liste passee a ConnectorLines
  // (hauteur de ligne unique), sans changer ce composant.
  const ganttRows = useMemo(() => {
    const rows = [];
    let rootIdx = -1;
    let color = PHASE_COLORS[0];
    let openGroup = null;
    const closeGroup = () => {
      if (openGroup) {
        rows.push({ id: `__add_${openGroup.id}`, rowKind: 'add', parentTask: openGroup, color: openGroup.color });
        openGroup = null;
      }
    };
    flattenedTasks?.forEach((task) => {
      if ((task?.level || 0) === 0) {
        closeGroup();
        rootIdx += 1;
        color = PHASE_COLORS[rootIdx % PHASE_COLORS.length];
        const isGroup = !!task?.hasChildren;
        const children = task?.children || [];
        const groupProgress = isGroup && children.length > 0
          ? Math.round(children.reduce((sum, c) => sum + Math.max(0, Math.min(100, Number(c?.progress) || 0)), 0) / children.length)
          : null;
        const row = { ...task, rowKind: isGroup ? 'group' : 'task', color, groupProgress };
        rows.push(row);
        if (isGroup && expandedTaskIds?.has(task?.id)) openGroup = row;
      } else {
        rows.push({ ...task, rowKind: 'task', color });
      }
    });
    closeGroup();
    rows.push({ id: '__add_root', rowKind: 'add', parentTask: null, color: null });
    return rows;
  }, [flattenedTasks, expandedTaskIds]);

  // ── Deplier / replier tout ───────────────────────────────────────────────
  const handleExpandAll = useCallback(() => {
    setExpandedTaskIds(new Set(tasks?.filter((t) => t?.parent_id)?.map((t) => t?.parent_id)));
  }, [tasks]);
  const handleCollapseAll = useCallback(() => {
    setExpandedTaskIds(new Set());
  }, []);

  // ── Colonnes visibles (menu ⚙) ───────────────────────────────────────────
  const [visibleCols, setVisibleCols] = useState({ start: true, end: true, dur: true, pct: true, assigned: false, status: false });
  const handleToggleCol = useCallback((key) => {
    setVisibleCols((prev) => ({ ...prev, [key]: !prev?.[key] }));
  }, []);

  // ── Panel drag handle ────────────────────────────────────────────────────
  const handlePanelDragMouseDown = useCallback((e) => {
    e?.preventDefault();
    const container = containerRef?.current;
    if (!container) return;

    const containerRect = container?.getBoundingClientRect();
    const startX = e?.clientX;
    const startPct = leftPanelWidth;

    const onMove = (me) => {
      const dx = me?.clientX - startX;
      const containerWidth = containerRect?.width;
      const newPct = startPct + (dx / containerWidth) * 100;
      const minPct = (300 / containerWidth) * 100;
      const maxPct = 75;
      setLeftPanelWidth(Math.min(maxPct, Math.max(minPct, newPct)));
    };

    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [leftPanelWidth]);

  // Edit task modal state
  const [editTask, setEditTask] = useState(null);
  const [depToDelete, setDepToDelete] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState('');

  const [dragConn, setDragConn] = useState(null);
  const [dragPos, setDragPos] = useState(null);

  const [form, setForm] = useState({
    name: '',
    subcontractor_id: '',
    start_date: '',
    end_date: '',
    progress: '',
    parent_id: null,
    parent_name: null,
    isJalon: false,
  });

  const [zoomLevel, setZoomLevel] = useState('day');
  const DAY_WIDTH = ZOOM_LEVELS[zoomLevel].dayWidth;

  // Ligne de temps : des dates des taches (14 j avant, 30 j apres, 90 j minimum) ;
  // sans tache datee, le 1er du mois courant sur 90 j.
  const { timelineStart: timelineStartDate, timelineDays } = useMemo(() => {
    const fallback = () => {
      const d = new Date();
      d.setDate(1);
      d.setHours(0, 0, 0, 0);
      return { timelineStart: d, timelineDays: 90 };
    };
    if (!tasks || tasks.length === 0) return fallback();
    const allDates = tasks.flatMap(t => [t.start_date, t.end_date].filter(Boolean));
    if (allDates.length === 0) return fallback();
    // 'T00:00:00' : une date nue serait lue en UTC et reculerait d'un jour au Quebec.
    const minDate = new Date(allDates.reduce((a, b) => a < b ? a : b) + 'T00:00:00');
    const maxDate = new Date(allDates.reduce((a, b) => a > b ? a : b) + 'T00:00:00');
    // Padding: 14 jours avant, 30 jours après
    minDate.setDate(minDate.getDate() - 14);
    maxDate.setDate(maxDate.getDate() + 30);
    const totalDays = Math.max(90, diffDays(toISO(minDate), toISO(maxDate)) + 1);
    return { timelineStart: minDate, timelineDays: totalDays };
  }, [tasks]);

  // Garder un ref pour que GanttBar et ConnectorLines puissent l'utiliser
  const timelineStart = useRef(timelineStartDate);
  timelineStart.current = timelineStartDate;

  const TIMELINE_DAYS = timelineDays;
  const days = buildDays(timelineStart.current, TIMELINE_DAYS);

  const ROW_HEIGHT = 32;
  const HEADER_HEIGHT = 40;

  useEffect(() => {
    if (id) fetchAll();
  }, [id]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [projectData, tasksResult, subcontractsData, depsResult] = await Promise.all([
        projectService?.getById(id),
        listTasksByProject(id),
        subcontractService?.getByProjectId(id),
        listDependenciesByProject(id),
      ]);
      setProject(projectData);
      setTasks(tasksResult?.data || []);
      setDependencies(depsResult?.data || []);

      const seen = new Set();
      const uniqueSubs = [];
      (subcontractsData || [])?.forEach((sc) => {
        if (sc?.subcontractor && !seen?.has(sc?.subcontractor?.id)) {
          seen?.add(sc?.subcontractor?.id);
          uniqueSubs?.push({
            id: sc?.subcontractor?.id,
            name: sc?.subcontractor?.companyName || sc?.subcontractor?.company_name || sc?.subcontractor?.name || '—',
          });
        }
      });
      setSubcontractors(uniqueSubs);
    } catch (err) {
      console.error('Error loading schedule data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (parentTask = null, options = {}) => {
    setForm({
      name: '',
      subcontractor_id: '',
      start_date: '',
      end_date: '',
      progress: '',
      parent_id: parentTask ? parentTask?.id : null,
      parent_name: parentTask ? parentTask?.name : null,
      isJalon: !!options?.milestone,
    });
    setFormError('');
    setShowModal(true);
  };

  // Rangee d'ajout : Tache / Jalon sous le groupe, Section = nouvelle tache racine.
  const handleAddRow = (kind, parentTask) => {
    if (kind === 'section') { handleOpenModal(null); return; }
    handleOpenModal(parentTask || null, { milestone: kind === 'milestone' });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormError('');
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setFormError('');
    if (!form?.name?.trim()) { setFormError(t('schedule.errors.nameRequired')); return; }
    if (!form?.start_date) { setFormError(t('schedule.errors.startRequired')); return; }
    // Jalon : la date de fin est forcee a la date de debut, pas de validation.
    if (!form?.isJalon) {
      if (!form?.end_date) { setFormError(t('schedule.errors.endRequired')); return; }
      if (form?.end_date < form?.start_date) { setFormError(t('schedule.errors.endBeforeStart')); return; }
    }

    setSubmitting(true);
    try {
      const payload = {
        project_id: id,
        name: form?.name?.trim(),
        subcontractor_id: form?.subcontractor_id || null,
        start_date: form?.start_date,
        end_date: form?.isJalon ? form?.start_date : form?.end_date,
        progress: form?.progress !== '' ? parseFloat(form?.progress) : 0,
        parent_id: form?.parent_id || null,
        // Nouvelle tache : en fin de son groupe (meme parent).
        sort_order: (tasks || [])
          ?.filter((t) => (t?.parent_id || null) === (form?.parent_id || null))
          ?.reduce((max, t) => Math.max(max, t?.sort_order ?? 0), 0) + 10,
      };
      const { error } = await createTask(payload);
      if (error) { setFormError(error?.message || t('schedule.errors.createFailed')); return; }
      // Auto-expand parent so new subtask is visible
      if (form?.parent_id) {
        setExpandedTaskIds((prev) => {
          const next = new Set(prev);
          next?.add(form?.parent_id);
          return next;
        });
      }
      handleCloseModal();
      await fetchAll();
    } catch (err) {
      setFormError(t('schedule.errors.unexpected'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditTask = (task) => {
    setEditTask(task);
    setEditForm({
      name: task?.name || '',
      subcontractor_id: task?.subcontractor_id || '',
      start_date: task?.start_date || '',
      end_date: task?.end_date || '',
      progress: task?.progress !== undefined ? String(task?.progress) : '0',
      isJalon: isMilestone(task),
    });
    setEditError('');
  };

  const handleCloseEditModal = () => {
    setEditTask(null);
    setEditError('');
  };

  const handleEditChange = (e) => {
    const { name, value } = e?.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async (e) => {
    e?.preventDefault();
    setEditError('');
    if (!editForm?.name?.trim()) { setEditError(t('schedule.errors.nameRequired')); return; }
    if (!editForm?.start_date) { setEditError(t('schedule.errors.startRequired')); return; }
    if (!editForm?.isJalon) {
      if (!editForm?.end_date) { setEditError(t('schedule.errors.endRequired')); return; }
      if (editForm?.end_date < editForm?.start_date) { setEditError(t('schedule.errors.endBeforeStart')); return; }
    }

    setEditSubmitting(true);
    try {
      const updates = {
        name: editForm?.name?.trim(),
        subcontractor_id: editForm?.subcontractor_id || null,
        start_date: editForm?.start_date,
        end_date: editForm?.isJalon ? editForm?.start_date : editForm?.end_date,
        progress: editForm?.progress !== '' ? parseFloat(editForm?.progress) : 0,
      };
      const { error } = await updateTask(editTask?.id, updates);
      if (error) { setEditError(error?.message || t('schedule.errors.updateFailed')); return; }
      handleCloseEditModal();
      await fetchAll();
    } catch (err) {
      setEditError(t('schedule.errors.unexpected'));
    } finally {
      setEditSubmitting(false);
    }
  };

  const cascadeShift = useCallback(async (movedTaskId, newEndDate, allTasks, allDeps) => {
    // Seules les dependances FS decalent leurs successeurs (pas de cascade FF/SS/SF pour l'instant).
    const directDependents = allDeps?.filter((d) => d?.depends_on_task_id === movedTaskId && (d?.type || 'FS') === 'FS');
    if (!directDependents?.length) return { updatedTasks: allTasks, updatedDeps: allDeps };

    let currentTasks = [...allTasks];
    let currentDeps = [...allDeps];

    for (const dep of directDependents) {
      const depTask = currentTasks?.find((t) => t?.id === dep?.task_id);
      if (!depTask) continue;

      // Durees inclusives : le successeur commence le lendemain de la fin.
      if (depTask?.start_date <= newEndDate) {
        const duration = depTask?.duration_days || durationDays(depTask?.start_date, depTask?.end_date) || 1;
        const newStart = addDays(newEndDate, 1);
        const newEnd = endForDuration(newStart, duration);

        const { data } = await updateTask(depTask?.id, { start_date: newStart, end_date: newEnd });
        if (data) {
          currentTasks = currentTasks?.map((t) => (t?.id === depTask?.id ? { ...t, ...data } : t));
          const result = await cascadeShift(depTask?.id, newEnd, currentTasks, currentDeps);
          currentTasks = result?.updatedTasks;
          currentDeps = result?.updatedDeps;
        }
      }
    }

    return { updatedTasks: currentTasks, updatedDeps: currentDeps };
  }, []);

  // Premier / dernier de chaque groupe de freres : leur ▲ / ▼ est desactive.
  const { firstSiblingIds, lastSiblingIds } = useMemo(() => {
    const groups = {};
    (tasks || [])?.forEach((t) => {
      const key = t?.parent_id || '__root__';
      (groups[key] ||= [])?.push(t);
    });
    const firsts = new Set();
    const lasts = new Set();
    Object.values(groups)?.forEach((siblings) => {
      const sorted = [...siblings]?.sort((a, b) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0));
      if (sorted?.length > 0) {
        firsts?.add(sorted?.[0]?.id);
        lasts?.add(sorted?.[sorted?.length - 1]?.id);
      }
    });
    return { firstSiblingIds: firsts, lastSiblingIds: lasts };
  }, [tasks]);

  // Monter / descendre une tache parmi ses freres (meme parent_id). On echange
  // les deux places, puis on renumerote le groupe par pas de 10 : deux freres a
  // sort_order egal (ex. 0 par defaut) ne bloquent ainsi jamais l'echange.
  // Seules les lignes dont le rang change sont ecrites.
  const handleMoveTask = useCallback(async (taskId, direction) => {
    const task = tasks?.find((t) => t?.id === taskId);
    if (!task) return;
    const siblings = tasks
      ?.filter((t) => (t?.parent_id || null) === (task?.parent_id || null))
      ?.sort((a, b) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0));
    const idx = siblings?.findIndex((s) => s?.id === taskId);
    const neighborIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (idx < 0 || neighborIdx < 0 || neighborIdx >= siblings?.length) return;

    const reordered = [...siblings];
    [reordered[idx], reordered[neighborIdx]] = [reordered[neighborIdx], reordered[idx]];
    const updates = reordered
      ?.map((t, i) => ({ id: t?.id, sort_order: (i + 1) * 10, previous: t?.sort_order }))
      ?.filter((u) => u?.sort_order !== u?.previous)
      ?.map(({ id: taskRowId, sort_order }) => ({ id: taskRowId, sort_order }));
    if (!updates?.length) return;

    // Mise a jour optimiste, puis persistance ; relecture complete si erreur.
    const newOrder = Object.fromEntries(updates?.map((u) => [u?.id, u?.sort_order]));
    setTasks((prev) => prev?.map((t) => (newOrder?.[t?.id] != null ? { ...t, sort_order: newOrder?.[t?.id] } : t)));
    const { error } = await batchUpdateSortOrder(updates);
    if (error) {
      console.error('Error reordering tasks:', error);
      await fetchAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks]);

  const handleUpdateTask = useCallback(async (taskId, updates) => {
    setTasks((prev) => prev?.map((t) => (t?.id === taskId ? { ...t, ...updates } : t)));

    const { data, error } = await updateTask(taskId, updates);
    if (!error && data) {
      setTasks((prev) => prev?.map((t) => (t?.id === taskId ? { ...t, ...data } : t)));

      if (updates?.end_date || updates?.start_date) {
        const movedTask = data;
        if (movedTask) {
          let currentTasks = await listTasksByProject(id);
          const { updatedTasks: shifted } = await cascadeShift(taskId, movedTask?.end_date, currentTasks?.data || [], dependencies);
          setTasks(shifted);
        }
      }
    } else if (error) {
      await fetchAll();
    }
  }, [dependencies, cascadeShift, id]);

  const handleDotMouseDown = useCallback((e, task, dotSide) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragConn({ fromTask: task, fromDot: dotSide, startX: e?.clientX, startY: e?.clientY });
    setDragPos({ x: e?.clientX, y: e?.clientY });
  }, []);

  const handleDeleteDep = useCallback((dep) => {
    setDepToDelete(dep);
  }, []);

  const confirmDeleteDep = useCallback(async () => {
    if (!depToDelete) return;
    const { error } = await deleteDependency(depToDelete?.id);
    if (!error) {
      const { data: newDeps } = await listDependenciesByProject(id);
      setDependencies(newDeps || []);
    }
    setDepToDelete(null);
  }, [depToDelete, id]);

  useEffect(() => {
    if (!dragConn) return;

    const onMove = (e) => {
      setDragPos({ x: e?.clientX, y: e?.clientY });
    };

    const onUp = async (e) => {
      setDragConn(null);
      setDragPos(null);

      const el = document.elementFromPoint(e?.clientX, e?.clientY);
      // Cible : un point de connexion, sinon la ligne de la tache.
      const dotEl = el?.closest('[data-dot-side]');
      const targetBarEl = dotEl?.closest('[data-task-id]') || el?.closest('[data-task-id]');
      if (!targetBarEl) return;

      const targetTaskId = targetBarEl?.getAttribute('data-task-id');
      if (!targetTaskId || targetTaskId === dragConn?.fromTask?.id) return;

      // Type deduit des deux points : depart (fromDot) -> arrivee (toDot, 'start' si lache sur la barre).
      const fromDot = dragConn?.fromDot;
      const toDot = dotEl?.getAttribute('data-dot-side') || 'start';
      let depType = 'FS';
      if (fromDot === 'end' && toDot === 'start') depType = 'FS';
      else if (fromDot === 'end' && toDot === 'end') depType = 'FF';
      else if (fromDot === 'start' && toDot === 'start') depType = 'SS';
      else if (fromDot === 'start' && toDot === 'end') depType = 'SF';

      const { error } = await createDependency(targetTaskId, dragConn?.fromTask?.id, depType);
      if (!error) {
        const { data: newDeps } = await listDependenciesByProject(id);
        setDependencies(newDeps || []);

        // Decalage automatique seulement pour FS.
        if (depType === 'FS') {
          const targetTask = tasks?.find((t) => t?.id === targetTaskId);
          const fromTask = dragConn?.fromTask;
          if (targetTask && fromTask && targetTask?.start_date <= fromTask?.end_date) {
            const duration = targetTask?.duration_days || durationDays(targetTask?.start_date, targetTask?.end_date) || 1;
            const newStart = addDays(fromTask?.end_date, 1);
            const newEnd = endForDuration(newStart, duration);
            await handleUpdateTask(targetTaskId, { start_date: newStart, end_date: newEnd });
          }
        }
      }
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragConn, tasks, id, handleUpdateTask]);

  const monthGroups = (() => {
    const groups = [];
    let current = null;
    days?.forEach((d, i) => {
      const key = `${d?.getFullYear()}-${d?.getMonth()}`;
      if (!current || current?.key !== key) {
        current = { key, label: d?.toLocaleDateString(localeOf(language), { month: 'long', year: 'numeric' }), start: i, count: 1 };
        groups?.push(current);
      } else {
        current.count++;
      }
    });
    return groups;
  })();

  const today = toISO(new Date());

  // A l'ouverture du Gantt : la colonne d'aujourd'hui est centree dans la vue.
  const ganttScrollRef = useRef(null);
  useEffect(() => {
    if (viewMode !== 'gantt' || loading) return;
    const el = ganttScrollRef?.current;
    if (!el || !timelineStart?.current) return;
    const offset = diffDays(toISO(timelineStart?.current), today);
    if (offset < 0 || offset >= TIMELINE_DAYS) return;
    el.scrollLeft = Math.max(0, offset * DAY_WIDTH - el.clientWidth / 2 + DAY_WIDTH / 2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, loading, zoomLevel]);

  const scrollToToday = () => {
    const el = ganttScrollRef?.current;
    if (!el || !timelineStart?.current) return;
    const offset = diffDays(toISO(timelineStart.current), today);
    if (offset >= 0 && offset < TIMELINE_DAYS) {
      el.scrollLeft = Math.max(0, offset * DAY_WIDTH - el.clientWidth / 2 + DAY_WIDTH / 2);
    }
  };

  // Zoom : la date au centre de la vue reste au centre.
  const handleZoomChange = useCallback((newLevel) => {
    const el = ganttScrollRef?.current;
    if (!el || !timelineStart?.current) {
      setZoomLevel(newLevel);
      return;
    }
    const oldDayWidth = ZOOM_LEVELS[zoomLevel].dayWidth;
    const centerX = el.scrollLeft + el.clientWidth / 2;
    const centerDayOffset = centerX / oldDayWidth;

    setZoomLevel(newLevel);

    // Après render, scroller pour garder cette date au centre
    requestAnimationFrame(() => {
      const newDayWidth = ZOOM_LEVELS[newLevel].dayWidth;
      el.scrollLeft = Math.max(0, centerDayOffset * newDayWidth - el.clientWidth / 2);
    });
  }, [zoomLevel]);

  // Defilement vertical synchronise : tableau (gauche) <-> Gantt (droite).
  const tableScrollRef = useRef(null);
  const isSyncing = useRef(false);
  const hasTasks = tasks?.length > 0;
  useEffect(() => {
    if (viewMode !== 'gantt' || loading) return;
    const left = tableScrollRef?.current;
    const right = ganttScrollRef?.current;
    if (!left || !right) return;

    const syncLeft = () => {
      if (isSyncing.current) return;
      isSyncing.current = true;
      right.scrollTop = left.scrollTop;
      isSyncing.current = false;
    };
    const syncRight = () => {
      if (isSyncing.current) return;
      isSyncing.current = true;
      left.scrollTop = right.scrollTop;
      isSyncing.current = false;
    };

    left.addEventListener('scroll', syncLeft);
    right.addEventListener('scroll', syncRight);
    return () => {
      left.removeEventListener('scroll', syncLeft);
      right.removeEventListener('scroll', syncRight);
    };
  }, [viewMode, loading, hasTasks]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <Icon name="Loader2" size={32} className="animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div
      className="flex flex-col h-full space-y-3"
      style={{ '--bg-group': '#f3f4f6', '--progress-bg': '#e5e7eb', '--fg-link': '#2563eb' }}
    >
      {/* Page Header */}
      <div className="flex items-start justify-between flex-shrink-0">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Icon name="CalendarDays" size={18} className="text-primary" />
            <h1 className="text-lg font-semibold text-[#2C3E50]">{t('schedule.title')}</h1>
          </div>
          {project && (
            <div>
              <div className="flex items-center gap-1 mb-1">
                <span
                  onClick={() => navigate(`/project-detail`)}
                  className="text-sm text-primary font-normal hover:text-primary-dark cursor-pointer transition-colors"
                >
                  {project?.projectNumber || ''}
                </span>
                <span className="text-sm text-primary font-normal">-</span>
                <span
                  onClick={() => navigate(`/project-detail`)}
                  className="text-sm text-primary font-normal hover:text-primary-dark cursor-pointer transition-colors"
                >
                  {project?.name || project?.project_name || ''}
                </span>
              </div>
              <p className="text-xs text-gray-500">{t('schedule.subtitle')}</p>
            </div>
          )}
        </div>

        <div className="flex items-center space-x-3">
          {/* View Toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('gantt')}
              className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${viewMode === 'gantt' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <Icon name="BarChart2" size={14} />
              <span>{t('schedule.viewGantt')}</span>
            </button>
            <button
              onClick={() => setViewMode('week')}
              className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${viewMode === 'week' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <Icon name="CalendarDays" size={14} />
              <span>{t('schedule.viewWeek')}</span>
            </button>
          </div>

          <button
            onClick={handleOpenModal}
            className="flex items-center space-x-2 bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            <Icon name="Plus" size={16} />
            <span>{t('schedule.addTask')}</span>
          </button>
        </div>
      </div>
      {/* Barre d'outils Gantt */}
      {viewMode === 'gantt' && tasks?.length > 0 && (
        <div className="flex items-center gap-0.5 flex-wrap flex-shrink-0 text-[11px]">
          <button type="button" onClick={handleExpandAll} className={TOOLBAR_BTN}>▸ {t('schedule.expandAll')}</button>
          <button type="button" onClick={handleCollapseAll} className={TOOLBAR_BTN}>▾ {t('schedule.collapseAll')}</button>
          <div className="w-px h-4 bg-gray-200 mx-1.5" />
          <button type="button" aria-disabled="true" title={t('schedule.comingSoon')} className={TOOLBAR_BTN_SOON}>⫧ {t('schedule.filter')}</button>
          <button type="button" aria-disabled="true" title={t('schedule.comingSoon')} className={TOOLBAR_BTN_SOON}>⇅ {t('schedule.sort')}</button>
          <div className="ml-auto flex items-center gap-1">
            <button type="button" onClick={scrollToToday} className={TOOLBAR_BTN} title={t('schedule.zoom.today')}>
              {t('schedule.zoom.today')}
            </button>
            <div className="w-px h-4 bg-gray-200 mx-1.5" />
            <span className="text-[10px] text-gray-400">{t('schedule.zoomLabel')}</span>
            <input
              type="range"
              min="0"
              max={ZOOM_ORDER.length - 1}
              step="1"
              value={Math.max(0, ZOOM_ORDER.indexOf(zoomLevel))}
              onChange={(e) => handleZoomChange(ZOOM_ORDER[Number(e?.target?.value)])}
              className="w-20 accent-gray-500 cursor-pointer"
              aria-label={t('schedule.zoomLabel')}
              title={t(`schedule.zoom.${zoomLevel}`)}
            />
            <span className="text-[10px] text-gray-500 w-14">{t(`schedule.zoom.${zoomLevel}`)}</span>
            <div className="w-px h-4 bg-gray-200 mx-1.5" />
            <button type="button" aria-disabled="true" title={t('schedule.comingSoon')} className={TOOLBAR_BTN_SOON}>↓ {t('schedule.export')}</button>
          </div>
        </div>
      )}
      {/* Empty state */}
      {tasks?.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 flex flex-col items-center justify-center py-16 text-gray-400">
          <Icon name="CalendarDays" size={40} className="mb-3 text-gray-300" />
          <p className="text-base font-medium text-gray-500">{t('schedule.noTasks')}</p>
          <p className="text-sm mt-1">{t('schedule.noTasksHint')}</p>
        </div>
      ) : viewMode === 'week' ? (
        <WeekView
          tasks={tasks}
          subcontractors={subcontractors}
          taskColorMap={tasks?.reduce((acc, t, i) => { acc[t.id] = getColor(i); return acc; }, {})}
          onEditTask={handleEditTask}
        />
      ) : (
        /* ── Gantt view: resizable left + right panels ── */
        (<div
          ref={containerRef}
          className="flex bg-white rounded-xl border border-gray-200 overflow-hidden flex-1 min-h-0"
          style={{ userSelect: panelDragRef?.current ? 'none' : undefined }}
        >
          {/* LEFT — Task Table (resizable) */}
          <div
            className="flex-shrink-0 flex flex-col overflow-hidden"
            style={{ width: `${leftPanelWidth}%`, minWidth: 300 }}
          >
            <TaskTable
              rows={ganttRows}
              subcontractors={subcontractors}
              rowHeight={ROW_HEIGHT}
              headerHeight={HEADER_HEIGHT}
              onUpdateTask={handleUpdateTask}
              expandedIds={expandedTaskIds}
              onToggleExpand={handleToggleExpand}
              onAddRow={handleAddRow}
              onMoveTask={handleMoveTask}
              firstSiblingIds={firstSiblingIds}
              lastSiblingIds={lastSiblingIds}
              scrollRef={tableScrollRef}
              visibleCols={visibleCols}
              onToggleCol={handleToggleCol}
            />
          </div>
          {/* PANEL DRAG HANDLE */}
          <div
            className="flex-shrink-0 relative flex items-center justify-center bg-gray-100 hover:bg-orange-100 transition-colors group"
            style={{ width: 5, cursor: 'col-resize', zIndex: 20 }}
            onMouseDown={handlePanelDragMouseDown}
            title={t('schedule.tooltips.dragToResizePanels')}
          >
            <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-gray-200 group-hover:bg-orange-300 transition-colors" />
            {/* grip dots */}
            <div className="flex flex-col gap-1 z-10">
              {[0,1,2]?.map((i) => (
                <div key={i} className="w-1 h-1 rounded-full bg-gray-300 group-hover:bg-orange-400 transition-colors" />
              ))}
            </div>
          </div>
          {/* RIGHT — Gantt Timeline (flex-1) */}
          <div className="flex-1 flex flex-col overflow-hidden min-w-0">
            <div ref={ganttScrollRef} className="overflow-x-auto overflow-y-auto flex-1">
              <div style={{ width: TIMELINE_DAYS * DAY_WIDTH, minWidth: '100%', position: 'relative' }}>
                {/* Month header */}
                <div className="flex bg-gray-50 sticky top-0 z-10" style={{ height: 18 }}>
                  {monthGroups?.map((mg) => (
                    <div key={mg?.key} className="flex-shrink-0 border-l border-gray-200 flex items-center px-1" style={{ width: mg?.count * DAY_WIDTH }}>
                      <span className="text-[10px] font-semibold text-gray-500 truncate">{mg?.label}</span>
                    </div>
                  ))}
                </div>

                {/* Day header */}
                <div className="flex bg-gray-50 border-b border-gray-200 sticky z-10" style={{ height: 22, top: 18 }}>
                  {days?.map((d, i) => {
                    const isToday = toISO(d) === today;
                    const isWeekend = d?.getDay() === 0 || d?.getDay() === 6;
                    // Libelle selon le zoom : chaque jour / les lundis / le 1er et le 15.
                    let dayLabel = '';
                    if (zoomLevel === 'day') {
                      dayLabel = d?.getDate();
                    } else if (zoomLevel === 'week') {
                      if (d?.getDay() === 1) dayLabel = d?.getDate();
                    } else {
                      const dayNum = d?.getDate();
                      if (dayNum === 1 || dayNum === 15) dayLabel = dayNum;
                    }
                    return (
                      <div
                        key={i}
                        className={`flex-shrink-0 flex items-center justify-center border-r border-gray-100 text-[10px] tabular-nums ${isToday ? 'bg-red-50 text-red-500 font-bold' : isWeekend ? 'text-gray-400' : 'text-gray-700 font-semibold'}`}
                        style={{ width: DAY_WIDTH }}
                      >
                        {dayLabel}
                      </div>
                    );
                  })}
                </div>

                {/* Task rows + connector lines — ganttRows : memes lignes que le tableau (rangees d'ajout incluses) */}
                <div style={{ position: 'relative' }}>
                  <ConnectorLines
                    tasks={ganttRows}
                    dependencies={dependencies}
                    timelineStart={timelineStart?.current}
                    rowHeight={ROW_HEIGHT}
                    headerHeight={0}
                    dayWidth={DAY_WIDTH}
                    onDeleteDep={handleDeleteDep}
                  />

                  {/* Today marker line */}
                  {(() => {
                    const todayOffset = diffDays(toISO(timelineStart.current), today);
                    if (todayOffset < 0 || todayOffset >= TIMELINE_DAYS) return null;
                    return (
                      <div
                        className="absolute top-0 bottom-0 pointer-events-none"
                        style={{
                          left: todayOffset * DAY_WIDTH + DAY_WIDTH / 2,
                          width: 2,
                          background: 'rgba(239, 68, 68, 0.6)',
                          zIndex: 6,
                        }}
                      />
                    );
                  })()}

                  {ganttRows?.map((task, idx) => {
                    const isAddRow = task?.rowKind === 'add';
                    return (
                      <div
                        key={task?.id}
                        data-task-id={isAddRow ? undefined : task?.id}
                        className="relative border-b border-gray-100 last:border-b-0"
                        style={{ height: ROW_HEIGHT, width: TIMELINE_DAYS * DAY_WIDTH }}
                      >
                        {/* Vertical grid lines */}
                        {days?.map((d, i) => {
                          const isToday = toISO(d) === today;
                          const isWeekend = d?.getDay() === 0 || d?.getDay() === 6;
                          return (
                            <div
                              key={i}
                              className={`absolute top-0 bottom-0 border-r ${isToday ? 'border-red-100 bg-red-50/30' : isWeekend ? 'border-gray-100 bg-gray-100/60' : 'border-gray-100/70'}`}
                              style={{ left: i * DAY_WIDTH, width: DAY_WIDTH }}
                            />
                          );
                        })}
                        {/* Task bar with dots */}
                        {!isAddRow && task?.start_date && task?.end_date && (
                          <GanttBar
                            task={task}
                            taskIndex={idx}
                            timelineStart={timelineStart?.current}
                            colorScheme={{ hex: task?.color }}
                            onUpdate={handleUpdateTask}
                            rowHeight={ROW_HEIGHT}
                            onDotMouseDown={handleDotMouseDown}
                            dayWidth={DAY_WIDTH}
                            variant={task?.rowKind === 'group' ? 'group' : 'task'}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>)
      )}
      {/* Supprimer une dependance (clic sur la ligne) */}
      <ConfirmDialog
        isOpen={!!depToDelete}
        title={t('schedule.dependency.deleteTitle')}
        message={t('schedule.dependency.deleteMessage')}
        confirmVariant="danger"
        confirmLabel={t('schedule.dependency.deleteConfirm')}
        cancelLabel={t('schedule.modal.cancel')}
        onConfirm={confirmDeleteDep}
        onCancel={() => setDepToDelete(null)}
      >
        <p className="text-xs text-gray-400 mt-2">
          {t('schedule.dependency.type')} : <span className="font-mono font-semibold">{depToDelete?.type || 'FS'}</span>
          {' — '}{t(`schedule.dependency.types.${depToDelete?.type || 'FS'}`)}
        </p>
      </ConfirmDialog>
      {/* Live drag-to-connect line */}
      {dragConn && dragPos && (
        <DragLine
          from={{ x: dragConn?.startX, y: dragConn?.startY }}
          to={dragPos}
        />
      )}
      {/* Add Task Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">{form?.parent_id ? t('schedule.modal.createSubtask') : t('schedule.modal.createTask')}</h2>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600 transition-colors">
                <Icon name="X" size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
              {formError && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-2">{formError}</div>
              )}

              {form?.parent_id && (
                <div className="flex items-center space-x-2 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                  <Icon name="CornerDownRight" size={14} className="text-orange-500 flex-shrink-0" />
                  <span className="text-xs text-orange-700 font-medium">
                    {t('schedule.modal.subtaskOf')} {form?.parent_name || t('schedule.modal.parentTask')}
                  </span>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('schedule.modal.taskName')} <span className="text-red-500">*</span></label>
                <input type="text" name="name" value={form?.name} onChange={handleChange} placeholder={t('schedule.modal.taskNamePlaceholder')} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('schedule.modal.subcontractor')}</label>
                <select name="subcontractor_id" value={form?.subcontractor_id} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white">
                  <option value="">— None —</option>
                  {subcontractors?.map((s) => <option key={s?.id} value={s?.id}>{s?.name}</option>)}
                </select>
              </div>

              <div title={t('schedule.modal.milestoneTooltip')}>
                <Checkbox
                  id="create-milestone"
                  checked={!!form?.isJalon}
                  onChange={(e) => setForm((prev) => ({ ...prev, isJalon: e?.target?.checked }))}
                  label={t('schedule.modal.milestone')}
                  description={t('schedule.modal.milestoneTooltip')}
                />
              </div>

              <div className={`grid ${form?.isJalon ? 'grid-cols-1' : 'grid-cols-2'} gap-3`}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('schedule.modal.startDate')} <span className="text-red-500">*</span></label>
                  <input type="date" name="start_date" value={form?.start_date} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
                </div>
                {!form?.isJalon && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('schedule.modal.endDate')} <span className="text-red-500">*</span></label>
                    <input type="date" name="end_date" value={form?.end_date} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('schedule.modal.progress')} <span className="text-gray-400 font-normal">— {t('schedule.modal.optional')}</span></label>
                <input type="number" name="progress" value={form?.progress} onChange={handleChange} min="0" max="100" step="1" placeholder="0" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
              </div>

              <div className="flex justify-end space-x-3 pt-2">
                <button type="button" onClick={handleCloseModal} className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">{t('schedule.modal.cancel')}</button>
                <button type="submit" disabled={submitting} className="flex items-center space-x-2 px-4 py-2 text-sm font-medium bg-primary hover:bg-primary-dark text-white rounded-lg transition-colors disabled:opacity-60">
                  {submitting && <Icon name="Loader2" size={14} className="animate-spin" />}
                  <span>{submitting ? t('schedule.modal.creating') : form?.parent_id ? t('schedule.modal.createSubtask') : t('schedule.modal.createTask')}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Edit Task Modal */}
      {editTask && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">{t('schedule.modal.editTask')}</h2>
              <button onClick={handleCloseEditModal} className="text-gray-400 hover:text-gray-600 transition-colors">
                <Icon name="X" size={20} />
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="px-6 py-5 space-y-4">
              {editError && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-2">{editError}</div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('schedule.modal.taskName')} <span className="text-red-500">*</span></label>
                <input type="text" name="name" value={editForm?.name} onChange={handleEditChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('schedule.modal.subcontractor')}</label>
                <select name="subcontractor_id" value={editForm?.subcontractor_id} onChange={handleEditChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white">
                  <option value="">— None —</option>
                  {subcontractors?.map((s) => <option key={s?.id} value={s?.id}>{s?.name}</option>)}
                </select>
              </div>

              <div title={t('schedule.modal.milestoneTooltip')}>
                <Checkbox
                  id="edit-milestone"
                  checked={!!editForm?.isJalon}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, isJalon: e?.target?.checked }))}
                  label={t('schedule.modal.milestone')}
                  description={t('schedule.modal.milestoneTooltip')}
                />
              </div>

              <div className={`grid ${editForm?.isJalon ? 'grid-cols-1' : 'grid-cols-2'} gap-3`}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('schedule.modal.startDate')} <span className="text-red-500">*</span></label>
                  <input type="date" name="start_date" value={editForm?.start_date} onChange={handleEditChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
                </div>
                {!editForm?.isJalon && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('schedule.modal.endDate')} <span className="text-red-500">*</span></label>
                    <input type="date" name="end_date" value={editForm?.end_date} onChange={handleEditChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('schedule.modal.progress')}</label>
                <input type="number" name="progress" value={editForm?.progress} onChange={handleEditChange} min="0" max="100" step="1" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
              </div>

              <div className="flex justify-end space-x-3 pt-2">
                <button type="button" onClick={handleCloseEditModal} className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">{t('schedule.modal.cancel')}</button>
                <button type="submit" disabled={editSubmitting} className="flex items-center space-x-2 px-4 py-2 text-sm font-medium bg-primary hover:bg-primary-dark text-white rounded-lg transition-colors disabled:opacity-60">
                  {editSubmitting && <Icon name="Loader2" size={14} className="animate-spin" />}
                  <span>{editSubmitting ? t('schedule.modal.saving') : t('schedule.modal.saveChanges')}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchedulePage;
